import type * as LDClient from '@launchdarkly/js-client-sdk';
import {
  ClientProviderEvents,
  ErrorCode,
  OpenFeatureEventEmitter,
} from '@openfeature/web-sdk';
import type {
  EvaluationContext,
  JsonValue,
  Logger,
  Provider,
  ProviderMetadata,
  ResolutionDetails,
} from '@openfeature/web-sdk';

import { InitializationError } from '../InitializationError';
import type { OnError } from '../types';
import { INITIALIZATION_TIMEOUT_SECONDS } from './initializationTimeout';

type LaunchDarklyProviderConstructorConfig = {
  ldClient: LDClient.LDClient;
  bootstrap?: LDClient.LDStartOptions['bootstrap'];
};

const FETCH_FAILURE_MESSAGES = new Set([
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
]);

// Sentry's fetch instrumentation appends the request host, e.g. "Failed to fetch (clientsdk.launchdarkly.com)".
const SENTRY_FETCH_HOST_SUFFIX = / \([^\s()]+\)$/;

const initializationFailureKind = (
  timedOut: boolean,
  error: unknown,
): 'timeout' | 'network error' | 'failed' => {
  if (timedOut) {
    return 'timeout';
  }
  if (
    error instanceof Error &&
    FETCH_FAILURE_MESSAGES.has(
      error.message.replace(SENTRY_FETCH_HOST_SUFFIX, ''),
    )
  ) {
    return 'network error';
  }
  return 'failed';
};

export class LaunchDarklyProvider implements Provider {
  public readonly metadata: ProviderMetadata = {
    name: 'LaunchDarklyProvider',
  };

  private readonly ldClient: LDClient.LDClient;
  private readonly bootstrap?: LDClient.LDStartOptions['bootstrap'];
  private readonly warnedFlags = new Set<string>();
  private startSettled = false;
  private startTimedOut = false;
  private sawInitVerdict = false;

  // MultiProvider's StatusTracker forwards this emitter's ConfigurationChanged
  // upward, so OpenFeature clients receive it via addHandler.
  public readonly events = new OpenFeatureEventEmitter();

  // The SDK emits `ready` exactly once, with no replay, the instant init
  // settles — possibly right after a timed-out start(), before any consumer has
  // had a chance to subscribe.
  //
  // `ready` means "init reached a verdict", NOT "values arrived": the SDK emits
  // it for both `complete` and `failed`, and gates only `initialized` on
  // success (`maybeSetInitializationResult` in js-client-sdk-common). A verdict
  // is what readiness needs — on `failed` the store stays empty and defaults
  // are the answer — so this listens to `ready` on purpose. It does mean a
  // late `failed` reports final values without any value having changed.
  //
  // The latch is recorded unconditionally, before the settled check: the
  // verdict can land in the same microtask batch as a timed-out start(), and
  // dropping it there would leave the provider reporting "not final" forever
  // with the outcome already known.
  //
  // The event only fires once start() has settled. Before that nobody has
  // subscribed yet, and the provider seeds itself from `hasFinalValues`.
  private readonly handleFlagsReady = () => {
    this.sawInitVerdict = true;

    if (!this.startSettled) {
      return;
    }

    this.events.emit(ClientProviderEvents.ConfigurationChanged);
  };

  /**
   * False only while a delivery is still outstanding: start() timed out without
   * values and `ready` has not landed. Lets consumers tell "these are the real
   * values" from "these are defaults, values may still arrive".
   *
   * Only a `timeout` defers the verdict — the SDK is still connecting. A
   * `failed` result is terminal, so defaults are already the answer and gating
   * on it would strand consumers until the deadline for nothing.
   */
  get hasFinalValues(): boolean {
    return !this.startTimedOut || this.sawInitVerdict;
  }

  constructor(
    config: LaunchDarklyProviderConstructorConfig,
    private onError?: OnError,
  ) {
    this.ldClient = config.ldClient;
    this.bootstrap = config.bootstrap;
  }

  async initialize(): Promise<void> {
    const startTime = Date.now();
    let clientStatusBefore = 'unknown';
    try {
      if (typeof this.ldClient.getContext === 'function') {
        clientStatusBefore = JSON.stringify(this.ldClient.getContext());
      }
    } catch {
      clientStatusBefore = 'unable to stringify context';
    }

    // Registered before start(): the SDK defers connecting until start() so
    // listeners can be in place for its once-only completion event.
    this.ldClient.on('ready', this.handleFlagsReady);

    // A timeout means the SDK stopped waiting, not that it stopped connecting;
    // `failed` and a thrown start() are terminal, with no late values to come.
    let timedOut = false;

    try {
      const result: LDClient.LDWaitForInitializationResult =
        await this.ldClient.start({
          timeout: INITIALIZATION_TIMEOUT_SECONDS,
          bootstrap: this.bootstrap,
        });
      if (result.status !== 'complete') {
        timedOut = result.status === 'timeout';
        throw result.status === 'failed' && result.error
          ? result.error
          : new Error(
              `LaunchDarkly initialization did not complete (status: ${result.status})`,
            );
      }
    } catch (error) {
      this.onError?.(
        new InitializationError(
          `[LaunchDarklyProvider] Failed to initialize: ${initializationFailureKind(timedOut, error)}`,
          {
            errorMessage:
              error instanceof Error ? error.message : String(error),
            errorType:
              error instanceof Error
                ? error.name
                : error?.constructor?.name || 'unknown',
            elapsedMs: Date.now() - startTime,
            timeoutSeconds: INITIALIZATION_TIMEOUT_SECONDS,
            online:
              typeof navigator !== 'undefined' ? navigator.onLine : undefined,
            clientContext: clientStatusBefore,
          },
          { cause: error },
        ),
      );
      // Don't rethrow - allow the MultiProvider to continue with other providers
      // The app will fall back to overrides, then flag defaults
    } finally {
      // Both latches are set here, not in the catch: onError is app-supplied
      // and may throw. Leaving `startTimedOut` unset on that path would make a
      // timed-out init report its defaults as final — the exact failure this
      // machinery exists to prevent.
      this.startTimedOut = timedOut;
      this.startSettled = true;
    }
  }

  async onClose(): Promise<void> {
    this.ldClient.off('ready', this.handleFlagsReady);
    await this.ldClient.close();
  }

  /**
   * Checks if a flag doesn't exist in LaunchDarkly based on the evaluation detail.
   * Returns true when the flag should trigger a fallback to the next provider.
   */
  private isFlagNotFound(detail: LDClient.LDEvaluationDetail): boolean {
    return (
      detail.reason?.kind === 'ERROR' ||
      (detail.variationIndex == null && detail.reason?.kind === 'OFF')
    );
  }

  private resolveEvaluation<T>(
    flagKey: string,
    defaultValue: T,
    logger: Logger,
    validateType: (value: unknown) => value is T,
    expectedType: string,
  ): ResolutionDetails<T> {
    try {
      const detail = this.ldClient.variationDetail(flagKey, defaultValue);
      const value = detail.value;

      // Check if flag doesn't exist in LaunchDarkly - trigger fallback to next provider
      if (this.isFlagNotFound(detail)) {
        if (!this.warnedFlags.has(flagKey)) {
          this.warnedFlags.add(flagKey);
          logger.warn(
            `Flag ${flagKey} not found in LaunchDarkly, falling back to next provider`,
          );
        }
        return {
          value: defaultValue,
          errorCode: ErrorCode.FLAG_NOT_FOUND,
          reason: 'ERROR',
          errorMessage: `Flag ${flagKey} not found in LaunchDarkly`,
        };
      }

      if (!validateType(value)) {
        logger.warn(
          `Flag ${flagKey} returned non-${expectedType} value: ${typeof value}`,
        );
        return {
          value: defaultValue,
          errorCode: ErrorCode.TYPE_MISMATCH,
          reason: 'ERROR',
          errorMessage: `Expected ${expectedType}, got ${typeof value}`,
        };
      }

      return {
        value,
        reason: this.mapReason(detail.reason),
        variant: detail.variationIndex?.toString(),
      };
    } catch (error) {
      this.onError?.(
        new Error(
          `Error evaluating ${expectedType} flag ${flagKey}: ${error instanceof Error ? error.message : String(error)}`,
        ),
      );
      return {
        value: defaultValue,
        errorCode: ErrorCode.GENERAL,
        reason: 'ERROR',
        errorMessage: error instanceof Error ? error.message : String(error),
      };
    }
  }

  resolveBooleanEvaluation(
    flagKey: string,
    defaultValue: boolean,
    _context: EvaluationContext,
    logger: Logger,
  ): ResolutionDetails<boolean> {
    return this.resolveEvaluation(
      flagKey,
      defaultValue,
      logger,
      (value): value is boolean => typeof value === 'boolean',
      'boolean',
    );
  }

  resolveStringEvaluation(
    flagKey: string,
    defaultValue: string,
    _context: EvaluationContext,
    logger: Logger,
  ): ResolutionDetails<string> {
    return this.resolveEvaluation(
      flagKey,
      defaultValue,
      logger,
      (value): value is string => typeof value === 'string',
      'string',
    );
  }

  resolveNumberEvaluation(
    flagKey: string,
    defaultValue: number,
    _context: EvaluationContext,
    logger: Logger,
  ): ResolutionDetails<number> {
    return this.resolveEvaluation(
      flagKey,
      defaultValue,
      logger,
      (value): value is number => typeof value === 'number',
      'number',
    );
  }

  resolveObjectEvaluation<T extends JsonValue>(
    flagKey: string,
    defaultValue: T,
    _context: EvaluationContext,
    logger: Logger,
  ): ResolutionDetails<T> {
    return this.resolveEvaluation(
      flagKey,
      defaultValue,
      logger,
      (value): value is T => typeof value === 'object' && value !== null,
      'object',
    );
  }

  private mapReason(
    ldReason?: LDClient.LDEvaluationReason,
  ):
    | 'STATIC'
    | 'DEFAULT'
    | 'TARGETING_MATCH'
    | 'SPLIT'
    | 'DISABLED'
    | 'UNKNOWN'
    | 'ERROR' {
    if (!ldReason) {
      return 'UNKNOWN';
    }

    switch (ldReason.kind) {
      case 'OFF':
        return 'DISABLED';
      case 'FALLTHROUGH':
      case 'TARGET_MATCH':
      case 'RULE_MATCH':
        return 'TARGETING_MATCH';
      case 'PREREQUISITE_FAILED':
      case 'ERROR':
        return 'ERROR';
      default:
        return 'UNKNOWN';
    }
  }
}
